/**
 * API Configuration & Axios Instance
 * Centralized API setup for the Destiny Junior PWA
 */
import axios from "axios";
import secureStorage from "../utils/secureStorage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Simple in-memory cache for API responses
const apiCache = new Map();
const CACHE_DURATION = 30 * 1000; // 30 seconds cache

export const getCachedData = (key) => {
  const cached = apiCache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }
  return null;
};

export const setCachedData = (key, data) => {
  apiCache.set(key, { data, timestamp: Date.now() });
};

export const clearCache = (keyPattern) => {
  if (keyPattern) {
    for (const key of apiCache.keys()) {
      if (key.includes(keyPattern)) {
        apiCache.delete(key);
      }
    }
  } else {
    apiCache.clear();
  }
};

export const axiosInstance = axios.create({
  baseURL: API,
});

// Flag to prevent multiple concurrent logouts
let isLoggingOut = false;

export const getIsLoggingOut = () => isLoggingOut;
export const setIsLoggingOut = (val) => { isLoggingOut = val; };

axiosInstance.interceptors.request.use((config) => {
  const token = secureStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor to cache GET requests (skip blob/arraybuffer responses)
axiosInstance.interceptors.response.use(
  (response) => {
    if (response.config.method === 'get' && !response.config.responseType) {
      const cacheKey = response.config.url;
      setCachedData(cacheKey, response.data);
    }
    return response;
  },
  (error) => {
    // Handle 401 Unauthorized - token expired or invalid
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      const isAuthEndpoint = url.includes('/auth/login') || 
                             url.includes('/auth/register') || 
                             url.includes('/auth/parent') ||
                             url.includes('/auth/token-status') ||
                             url.includes('/auth/refresh-token') ||
                             url.includes('/auth/me') ||
                             url.includes('/auth/forgot-password') ||
                             url.includes('/auth/reset-password') ||
                             url.includes('/auth/verify-email');
      
      if (!isAuthEndpoint && !isLoggingOut) {
        isLoggingOut = true;
        console.warn('[AUTH] Token expired or invalid, clearing session');
        secureStorage.removeItem('token');
        secureStorage.removeItem('user');
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login?expired=true';
        }
        setTimeout(() => { isLoggingOut = false; }, 3000);
      }
    }

    // Handle 403 BRANCH_DISABLED - cawangan dinonaktifkan sementara oleh superadmin
    if (error.response?.status === 403) {
      const detail = error.response?.data?.detail || '';
      if (typeof detail === 'string' && detail.startsWith('ACCESS_ENDED') && !isLoggingOut) {
        isLoggingOut = true;
        secureStorage.removeItem('token');
        secureStorage.removeItem('user');
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login?access_ended=true';
        }
        setTimeout(() => { isLoggingOut = false; }, 3000);
      }
      if (typeof detail === 'string' && detail.startsWith('BRANCH_DISABLED') && !isLoggingOut) {
        isLoggingOut = true;
        console.warn('[AUTH] Branch disabled by superadmin, clearing session');
        secureStorage.removeItem('token');
        secureStorage.removeItem('user');
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login?branch_disabled=true';
        }
        setTimeout(() => { isLoggingOut = false; }, 3000);
      }
    }
    // Normalkan respons ralat bukan-JSON (cth. halaman HTML Cloudflare 52x / 5xx proxy)
    // supaya semua `error.response?.data?.detail` di seluruh app memaparkan mesej mesra
    if (error.response) {
      const { data, status } = error.response;
      const isNonJson = typeof data !== 'object' || data === null;
      if (isNonJson || (status >= 500 && data?.detail === undefined)) {
        error.response.data = { detail: getApiErrorMessage(error), raw_status: status, non_json: isNonJson };
      }
    }
    return Promise.reject(error);
  }
);

// Tukar ralat axios kepada mesej mesra (termasuk respons bukan-JSON seperti halaman Cloudflare)
export const getApiErrorMessage = (error, fallback = "Ralat tidak diketahui") => {
  if (!error?.response) {
    return "Tiada sambungan ke pelayan. Sila semak internet dan cuba lagi.";
  }
  const { status, data } = error.response;
  const detail = data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (detail && typeof detail === 'object') {
    if (detail.message) return detail.message;
    if (Array.isArray(detail)) {
      const msgs = detail.map(d => d?.msg || d?.message).filter(Boolean);
      if (msgs.length) return msgs.join(', ');
    }
  }
  const isNonJson = typeof data === 'string' || data === undefined || data === null || data === '';
  if (status >= 520 && status <= 526) {
    return `Pelayan tidak memberi respons yang sah (ralat ${status}). Data mungkin telah disimpan — sila muat semula dan semak sebelum mencuba lagi.`;
  }
  if (status >= 500) {
    return `Pelayan tidak dapat memproses permintaan (ralat ${status}). Sila cuba sebentar lagi.`;
  }
  if (isNonJson) {
    return `Permintaan gagal (ralat ${status}). Sila cuba sebentar lagi.`;
  }
  return fallback;
};

// Loading fallback component - Mickey Head with Disneyland background
export const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen relative overflow-hidden">
    <div 
      className="absolute inset-0"
      style={{
        backgroundImage: 'url(/bg-disneyland.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center top',
        backgroundRepeat: 'no-repeat',
      }}
    />
    <div className="absolute inset-0" style={{
      background: 'linear-gradient(180deg, rgba(255,255,255,0.3) 0%, rgba(255,240,250,0.5) 40%, rgba(255,255,255,0.65) 70%, rgba(255,255,255,0.8) 100%)'
    }} />
    <div className="relative z-10 text-center">
      <div className="relative inline-block">
        <div 
          className="absolute inset-[-10px] rounded-full border-4 border-pink-200 border-t-pink-500"
          style={{ animation: 'spin 1.2s linear infinite' }}
        />
        <div style={{ animation: 'mickeyBounce 2s ease-in-out infinite' }}>
          <svg width="60" height="56" viewBox="0 0 200 180" style={{ filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.2))' }}>
            <circle cx="50" cy="38" r="38" fill="#1a1a1a"/>
            <circle cx="150" cy="38" r="38" fill="#1a1a1a"/>
            <circle cx="100" cy="108" r="60" fill="#1a1a1a"/>
            <clipPath id="pl-pants"><rect x="40" y="112" width="120" height="60"/></clipPath>
            <circle cx="100" cy="108" r="58" fill="#E31B23" clipPath="url(#pl-pants)"/>
            <rect x="42" y="108" width="116" height="5" rx="2.5" fill="#1a1a1a"/>
            <ellipse cx="80" cy="138" rx="9" ry="12" fill="#FFC72C"/>
            <ellipse cx="120" cy="138" rx="9" ry="12" fill="#FFC72C"/>
          </svg>
        </div>
      </div>
      <p className="mt-4 text-purple-700 text-sm font-bold animate-pulse" style={{ textShadow: '0 1px 4px rgba(255,255,255,0.8)' }}>Memuatkan...</p>
      <style>{`@keyframes mickeyBounce { 0%, 100% { transform: translateY(0) scale(1); } 50% { transform: translateY(-8px) scale(1.05); } }`}</style>
    </div>
  </div>
);
